import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, History, Pin, PinOff, Plus, X } from 'lucide-react';
import {
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  cn,
} from '@proerp/ui';
import { formatShortcut } from '@/lib/platformKeys';
import { isTypingTarget, SHORTCUT_KEYS } from '@/lib/shortcuts';
import { useKeyPlatform } from '@/lib/useKeyPlatform';
import { useUiStore } from '@/store/uiStore';
import { navLeaves } from './navLeaves';
import {
  closeOtherTabs,
  closeTab,
  closeTabsToRight,
  createTabId,
  deferTabChange,
  isTabDirty,
  moveTab,
  reopenClosedTab,
  tabKey,
  togglePinned,
  useOpenTabs,
  type OpenTab,
  type TabChange,
  type TabSwitchState,
  type TabsSnapshot,
} from './openTabs';
import { recentPlacesFor, useRecentPlaces } from './recentPlaces';
import { describeTab, type TabDescription } from './tabLabel';
import { useNavItems } from './useNavItems';

function currentSnapshot(): TabsSnapshot {
  const { tabs, activeId, closedPaths } = useOpenTabs.getState();
  return { tabs, activeId, closedPaths };
}

function switchState(tabId: string): TabSwitchState {
  return { tabSwitch: tabId };
}

/**
 * Tab-Leiste unter der oberen Leiste (ADR 0111, Variante A „klassisch"):
 * jede offene Seite ein Tab, das Dashboard angeheftet ganz links.
 *
 * Ein Tab ist ein echter Link auf seine Adresse (CLAUDE.md §9) — der Wechsel ist
 * eine normale Navigation, `useTabSync` erkennt den Tab daran wieder. Schließen,
 * Anheften und Sortieren ändern nur den Stand; wird dabei ein anderer Tab
 * aktiv, navigiert die Leiste dorthin.
 *
 * Ungespeicherte Änderungen (2026-09-30): Die Seite meldet sie (`useTabDirty`),
 * der Tab zeigt dann einen Punkt statt des ×. Schließen des aktiven Tabs ist
 * dann erst eine Navigation, die die Seite noch ablehnen kann (Rückfrage im
 * Beleg-Editor); der Tab verschwindet erst, wenn sie ankommt.
 *
 * Sitzt in der oberen Leiste (Topbar, Kopfzeile A 2026-09-30) und nimmt deren
 * Höhe; aktive Tabs reichen über den unteren Rand und schließen an den Inhalt an.
 * Erst ab `tablet` sichtbar; darunter übernimmt die MobileDock.
 */
export function TabBar() {
  const tabs = useOpenTabs((state) => state.tabs);
  const activeId = useOpenTabs((state) => state.activeId);
  const closedPaths = useOpenTabs((state) => state.closedPaths);
  const userId = useOpenTabs((state) => state.userId);
  const flash = useOpenTabs((state) => state.flash);
  const dirtyKeys = useOpenTabs((state) => state.dirtyKeys);
  const places = useRecentPlaces((state) => state.places);
  const openSearch = useUiStore((state) => state.setSearchOpen);
  const navItems = useNavItems();
  const navigate = useNavigate();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ id: string; overId: string | null; isAfter: boolean } | null>(null);

  const leaves = navLeaves(navItems);
  const recents = recentPlacesFor(places, userId ?? undefined);

  /**
   * Tabs ändern; ist danach ein anderer Tab aktiv, dorthin navigieren. Hat die
   * Seite des aktiven Tabs ungespeicherte Änderungen, kann sie das Verlassen
   * noch ablehnen — dann gilt die Änderung erst, wenn die Navigation ankommt
   * (`deferTabChange`, übernommen in `useTabSync`), und „Abbrechen" lässt alles,
   * wie es war. Sonst wie bisher sofort: Eine Seite, die ins Vollbild umleitet,
   * käme in der App-Hülle nie an.
   */
  const apply = useCallback(
    (change: TabChange) => {
      const current = currentSnapshot();
      const next = change(current);
      const active = next.tabs.find((tab) => tab.id === next.activeId);
      if (!active || active.id === current.activeId) {
        useOpenTabs.getState().commit(next);
        return;
      }
      const leaving = current.tabs.find((tab) => tab.id === current.activeId);
      if (leaving && isTabDirty(leaving.path)) {
        navigate(active.path, { state: { tabSwitch: active.id, tabChange: deferTabChange(change) } satisfies TabSwitchState });
        return;
      }
      useOpenTabs.getState().commit(next);
      navigate(active.path, { state: switchState(active.id) });
    },
    [navigate],
  );
  // Neue IDs einmal vorab: Die aufgeschobene Änderung muss beim Ankommen denselben
  // Tab ergeben, zu dem navigiert wurde.
  const close = useCallback(
    (id: string) => {
      const fallbackId = createTabId();
      apply((snapshot) => closeTab(snapshot, id, () => fallbackId));
    },
    [apply],
  );
  const reopen = useCallback(() => {
    const reopenedId = createTabId();
    apply((snapshot) => reopenClosedTab(snapshot, () => reopenedId)?.snapshot ?? snapshot);
  }, [apply]);

  // Tastatur (außerhalb von Eingabefeldern): Alt+1…8 Tab n, Alt+9 letzter, Alt+W schließen,
  // Alt+⇧+T wiederherstellen. ⌘/Strg+T und W gehören dem Browser; über `code`, weil Alt
  // auf dem Mac andere Zeichen liefert (⌥5 = „[").
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!event.altKey || event.metaKey || event.ctrlKey || isTypingTarget(event.target)) return;
      const state = useOpenTabs.getState();
      if (/^Digit[1-9]$/.test(event.code)) {
        const position = Number(event.code.slice(5));
        const tab = position === 9 ? state.tabs[state.tabs.length - 1] : state.tabs[position - 1];
        if (!tab) return;
        event.preventDefault();
        if (tab.id !== state.activeId) navigate(tab.path, { state: switchState(tab.id) });
      } else if (event.code === 'KeyW' && !event.shiftKey && state.activeId) {
        event.preventDefault();
        close(state.activeId);
      } else if (event.code === 'KeyT' && event.shiftKey) {
        event.preventDefault();
        reopen();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, close, reopen]);

  // Aktiven (bzw. gerade aufleuchtenden) Tab in den sichtbaren Bereich der Leiste holen —
  // nur waagerecht, damit die Seite selbst nicht springt.
  const focusId = flash?.id ?? activeId;
  useEffect(() => {
    const scroller = scrollerRef.current;
    const element = focusId ? scroller?.querySelector<HTMLElement>(`[data-tab-id="${focusId}"]`) : null;
    if (!scroller || !element) return;
    const left = element.offsetLeft;
    const right = left + element.offsetWidth;
    if (left < scroller.scrollLeft) scroller.scrollLeft = left - 8;
    else if (right > scroller.scrollLeft + scroller.clientWidth) scroller.scrollLeft = right - scroller.clientWidth + 8;
  }, [focusId, flash?.seq, tabs.length]);

  if (!userId || tabs.length === 0) return null;

  const described = tabs.map((tab) => ({
    tab,
    description: describeTab(tab, leaves, recents),
    isDirty: dirtyKeys.includes(tabKey(tab.path)),
  }));

  return (
    <nav
      aria-label="Offene Tabs"
      className="hidden min-w-0 flex-1 items-end gap-1 self-stretch tablet:flex"
    >
      <div
        ref={scrollerRef}
        className="relative flex h-full min-w-0 flex-1 items-end overflow-x-auto pt-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {described.map(({ tab, description, isDirty }, index) => (
          <TabItem
            key={tab.id}
            tab={tab}
            description={description}
            isActive={tab.id === activeId}
            isDirty={isDirty}
            flashSeq={flash?.id === tab.id ? flash.seq : null}
            dropMarker={drag?.overId === tab.id ? (drag.isAfter ? 'after' : 'before') : null}
            canCloseOthers={tabs.some((other) => other.id !== tab.id && !other.isPinned)}
            canCloseRight={tabs.slice(index + 1).some((other) => !other.isPinned)}
            canReopen={closedPaths.length > 0}
            onClose={() => close(tab.id)}
            onTogglePin={() => apply((snapshot) => togglePinned(snapshot, tab.id))}
            onCloseOthers={() => apply((snapshot) => closeOtherTabs(snapshot, tab.id))}
            onCloseRight={() => apply((snapshot) => closeTabsToRight(snapshot, tab.id))}
            onReopen={reopen}
            onDragStart={() => setDrag({ id: tab.id, overId: null, isAfter: false })}
            onDragOver={(isAfter) => {
              const dragged = drag && tabs.find((other) => other.id === drag.id);
              if (!drag || !dragged || dragged.id === tab.id || dragged.isPinned !== tab.isPinned) return false;
              if (drag.overId !== tab.id || drag.isAfter !== isAfter) setDrag({ ...drag, overId: tab.id, isAfter });
              return true;
            }}
            onDrop={() => {
              if (drag) apply((snapshot) => moveTab(snapshot, drag.id, tab.id, drag.isAfter));
              setDrag(null);
            }}
            onDragEnd={() => setDrag(null)}
          />
        ))}
        {/* „+" steht direkt hinter dem letzten Tab und wandert beim Scrollen mit — wie im Browser. */}
        <Button
          variant="ghost"
          size="icon"
          className="mb-1 ml-1 size-7 shrink-0 self-end text-muted-foreground touch:mb-1 touch:size-9 touch:min-h-0"
          aria-label="Neuer Tab"
          title="Neuer Tab: Seite oder Datensatz suchen"
          onClick={() => openSearch(true, { inNewTab: true })}
        >
          <Plus strokeWidth={1.5} />
        </Button>
      </div>
      <AllTabsMenu described={described} activeId={activeId} canReopen={closedPaths.length > 0} onReopen={reopen} />
    </nav>
  );
}

function TabItem({
  tab,
  description,
  isActive,
  isDirty,
  flashSeq,
  dropMarker,
  canCloseOthers,
  canCloseRight,
  canReopen,
  onClose,
  onTogglePin,
  onCloseOthers,
  onCloseRight,
  onReopen,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  tab: OpenTab;
  description: TabDescription;
  isActive: boolean;
  /** Ungespeicherte Änderungen: Punkt statt × (beim Überfahren wieder ×). */
  isDirty: boolean;
  flashSeq: number | null;
  dropMarker: 'before' | 'after' | null;
  canCloseOthers: boolean;
  canCloseRight: boolean;
  canReopen: boolean;
  onClose: () => void;
  onTogglePin: () => void;
  onCloseOthers: () => void;
  onCloseRight: () => void;
  onReopen: () => void;
  onDragStart: () => void;
  /** Darf hier abgelegt werden? (Nur innerhalb derselben Zone.) */
  onDragOver: (isAfter: boolean) => boolean;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const Icon = description.icon;
  const tooltip = [description.label, description.area, tab.isPinned ? 'angeheftet' : null].filter(Boolean).join(' · ');
  const keyPlatform = useKeyPlatform();
  const closeKeys = formatShortcut(SHORTCUT_KEYS.closeTab, keyPlatform);
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          data-tab-id={tab.id}
          draggable
          onDragStart={(event) => {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/uri-list', new URL(tab.path, window.location.origin).href);
            onDragStart();
          }}
          onDragOver={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            if (onDragOver(event.clientX > rect.left + rect.width / 2)) event.preventDefault();
          }}
          onDrop={(event) => {
            event.preventDefault();
            onDrop();
          }}
          onDragEnd={onDragEnd}
          // Mittelklick schließt — wie im Browser. `preventDefault`, damit ihn die
          // Link-Abfangung (`useTabSync`) nicht als „im Hintergrund öffnen" liest.
          onAuxClick={(event) => {
            if (event.button !== 1) return;
            event.preventDefault();
            onClose();
          }}
          className={cn(
            'group relative flex h-9 shrink items-center gap-1 rounded-t-lg border border-b-0 border-transparent text-sm text-muted-foreground touch:h-10',
            tab.isPinned ? 'w-11 flex-none justify-center' : 'min-w-28 flex-[0_1_13rem] pr-1',
            isActive ? 'z-[1] -mb-px border-border bg-background text-foreground' : 'hover:bg-background/60 hover:text-foreground',
          )}
        >
          {flashSeq !== null && (
            <span
              key={flashSeq}
              aria-hidden
              className="pointer-events-none absolute inset-0 animate-[tab-flash_1s_ease-out_forwards] rounded-t-lg bg-primary/15"
            />
          )}
          {dropMarker && (
            <span
              aria-hidden
              className={cn('pointer-events-none absolute inset-y-1.5 z-[2] w-0.5 rounded bg-foreground', dropMarker === 'after' ? '-right-px' : '-left-px')}
            />
          )}
          <Link
            to={tab.path}
            state={switchState(tab.id)}
            draggable={false}
            aria-current={isActive ? 'page' : undefined}
            title={tooltip}
            className={cn(
              'flex min-h-0 min-w-0 flex-1 items-center gap-2 self-stretch rounded-t-lg text-inherit no-underline outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
              tab.isPinned ? 'justify-center' : 'pl-3',
            )}
          >
            <Icon className="size-4 shrink-0" strokeWidth={1.5} aria-hidden />
            {tab.isPinned ? <span className="sr-only">{`${description.label} (angeheftet)`}</span> : <span className="truncate">{description.label}</span>}
            {isDirty && <span className="sr-only"> (ungespeichert)</span>}
            {isDirty && tab.isPinned && <DirtyDot className="absolute right-1.5 top-2.5" />}
          </Link>
          {!tab.isPinned && (
            <button
              type="button"
              onClick={onClose}
              aria-label={`${description.label} schließen`}
              title={isDirty ? `Ungespeicherte Änderungen · Schließen (${closeKeys})` : `Schließen (${closeKeys})`}
              data-dirty={isDirty || undefined}
              className={cn(
                'group/close grid size-5 min-h-0 shrink-0 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring touch:size-8',
                isActive || isDirty ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
              )}
            >
              {isDirty ? (
                <>
                  {/* Wie im Code-Editor: Punkt = ungespeichert; über dem Tab wird er zum ×. */}
                  <DirtyDot className="group-hover:hidden group-focus-visible/close:hidden" />
                  <X className="hidden size-3.5 group-hover:block group-focus-visible/close:block" strokeWidth={1.75} aria-hidden />
                </>
              ) : (
                <X className="size-3.5" strokeWidth={1.75} aria-hidden />
              )}
            </button>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-64">
        <ContextMenuItem onSelect={onTogglePin}>
          {tab.isPinned ? <PinOff strokeWidth={1.5} aria-hidden /> : <Pin strokeWidth={1.5} aria-hidden />}
          {tab.isPinned ? 'Loslösen' : 'Anheften'}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={onClose}>
          <X strokeWidth={1.5} aria-hidden />
          Schließen
          <span className="ml-auto text-xs text-muted-foreground">{closeKeys}</span>
        </ContextMenuItem>
        <ContextMenuItem disabled={!canCloseOthers} onSelect={onCloseOthers}>
          <span className="size-4" aria-hidden />
          Andere Tabs schließen
        </ContextMenuItem>
        <ContextMenuItem disabled={!canCloseRight} onSelect={onCloseRight}>
          <span className="size-4" aria-hidden />
          Tabs rechts schließen
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem disabled={!canReopen} onSelect={onReopen}>
          <History strokeWidth={1.5} aria-hidden />
          Geschlossenen Tab wiederherstellen
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

/** Markierung „ungespeicherte Änderungen" am Tab. */
function DirtyDot({ className }: { className?: string }) {
  return <span aria-hidden data-testid="tab-dirty" className={cn('size-2 shrink-0 rounded-full bg-foreground/70', className)} />;
}

/** „Alle Tabs" rechts: Liste aller offenen Tabs (Links) + Wiederherstellen — hilft, wenn die Leiste scrollt. */
function AllTabsMenu({
  described,
  activeId,
  canReopen,
  onReopen,
}: {
  described: { tab: OpenTab; description: TabDescription; isDirty: boolean }[];
  activeId: string | null;
  canReopen: boolean;
  onReopen: () => void;
}) {
  const keyPlatform = useKeyPlatform();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="mb-1 h-7 shrink-0 gap-1 self-end px-2 text-xs tabular-nums text-muted-foreground touch:mb-1.5 touch:h-9 touch:min-h-0"
          aria-label={`Alle ${described.length} Tabs anzeigen`}
          title="Alle Tabs"
        >
          {described.length}
          <ChevronDown className="size-3.5" strokeWidth={1.5} aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Offene Tabs</DropdownMenuLabel>
        {described.map(({ tab, description, isDirty }) => {
          const Icon = description.icon;
          return (
            <DropdownMenuItem key={tab.id} asChild>
              <Link to={tab.path} state={switchState(tab.id)} aria-current={tab.id === activeId ? 'page' : undefined}>
                <Icon strokeWidth={1.5} aria-hidden />
                <span className={cn('min-w-0 flex-1 truncate', tab.id === activeId && 'font-medium')}>{description.label}</span>
                {isDirty && <DirtyDot className="size-1.5" />}
                {isDirty && <span className="sr-only">(ungespeichert)</span>}
                {tab.isPinned && <Pin className="text-muted-foreground" strokeWidth={1.5} aria-label="angeheftet" />}
                {tab.id === activeId && <Check strokeWidth={1.5} aria-label="aktiv" />}
              </Link>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={!canReopen} onSelect={onReopen}>
          <History strokeWidth={1.5} aria-hidden />
          Geschlossenen Tab wiederherstellen
          <DropdownMenuShortcut>{formatShortcut(SHORTCUT_KEYS.reopenTab, keyPlatform)}</DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
